import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, Info } from 'lucide-react';
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter';
import { 
  validatePasswordStrength, 
  sanitizeEmail, 
  isValidEmail,
  generateBrowserFingerprint,
  STANDARD_PASSWORD_REQUIREMENTS,
} from '@/lib/security';
import { checkRateLimit, recordAuthAttempt } from '@/lib/rateLimiting';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { GoogleSignInButton } from './GoogleSignInButton';
import { AuthDivider } from './AuthDivider';

interface SignUpFormProps {
  onSuccess: () => void;
}

export function SignUpForm({ onSuccess }: SignUpFormProps) {
  const { signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const sanitizedEmail = sanitizeEmail(email);
    if (!isValidEmail(sanitizedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check rate limit before attempting
    const rateLimitCheck = checkRateLimit(sanitizedEmail, 'signup');
    if (!rateLimitCheck.allowed) {
      setRateLimitMessage(rateLimitCheck.message || 'Too many attempts. Please try again later.');
      return;
    }

    // Validate password strength
    const passwordCheck = validatePasswordStrength(password, STANDARD_PASSWORD_REQUIREMENTS);
    if (!passwordCheck.meetsAllRequirements) {
      toast.error(passwordCheck.feedback[0] || 'Password must be at least 8 characters long');
      return;
    }

    // Check password confirmation
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setRateLimitMessage(null);
    setIsLoading(true);
    
    const { error } = await signUp(sanitizedEmail, password);
    
    if (error) {
      recordAuthAttempt(sanitizedEmail, 'signup', false);
      
      // Sanitize error messages
      let safeMessage = 'Sign up failed. Please try again.';
      if (error.message.includes('already registered') || error.message.includes('already exists')) {
        safeMessage = 'An account may already exist with this email. Try signing in instead.';
      } else if (error.message.includes('weak') || error.message.includes('password')) {
        safeMessage = 'Password is too weak or has been found in a data breach. Please choose a different password.';
      }
      
      toast.error(safeMessage);
    } else {
      recordAuthAttempt(sanitizedEmail, 'signup', true);
      
      // Record signup metadata for abuse detection
      try {
        const fingerprint = generateBrowserFingerprint();
        localStorage.setItem('signupFingerprint', fingerprint);
      } catch (err) {
        console.log('Fingerprint generation failed:', err);
      }

      toast.success('Account created! Please check your email to verify your account before signing in.');
      onSuccess();
    }
    
    setIsLoading(false);
  };

  return (
    <div className="space-y-4">
      {/* Google Sign Up - Primary Option */}
      <GoogleSignInButton onSuccess={onSuccess} disabled={isLoading} />
      
      <AuthDivider />

      {/* Email/Password Form */}
      <form onSubmit={handleSignUp} className="space-y-4">
        {rateLimitMessage && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{rateLimitMessage}</AlertDescription>
          </Alert>
        )}

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Password must be at least 8 characters long.
          </AlertDescription>
        </Alert>
        
        <div className="space-y-2">
          <Label htmlFor="signup-email">Email</Label>
          <Input
            id="signup-email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            disabled={isLoading}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="signup-password">Password</Label>
          <Input
            id="signup-password"
            type="password"
            placeholder="Min 8 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            disabled={isLoading}
          />
          <PasswordStrengthMeter password={password} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="signup-confirm-password">Confirm Password</Label>
          <Input
            id="signup-confirm-password"
            type="password"
            placeholder="Re-enter your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            disabled={isLoading}
          />
          {confirmPassword && password !== confirmPassword && (
            <p className="text-xs text-destructive">Passwords do not match</p>
          )}
        </div>
        <Button type="submit" className="w-full h-11" disabled={isLoading || !!rateLimitMessage}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create Account with Email
        </Button>
        
        <p className="text-xs text-muted-foreground text-center">
          By signing up, you agree to our terms and privacy policy. Email verification required for email/password signup.
        </p>
      </form>
    </div>
  );
}
